// The op card (APPLE-SPEC §6.3; builder §15.4, §16): what the agent changed on a screen, written for people, as a white hairline group
// (radius 8). Header: a 14 px glyph, the human title (12.5 / 600) and "rev 41" in the secondary label. Body: inspector key/value rows
// (62 px end-aligned keys). A closed "Technical details" disclosure. Footer: the state as text at the start (✓ Applied for you ·
// ✦ Draft · not applied · ⌛ Awaiting the Director …), then ONE action group at the end that wraps as a unit, the default action last:
// Undo · ⇄ · Propose to department / Preview · Send for approval / Open the inbox. Endpoint paths are never shown. A staged action of the
// thread (a confirmation) reads the same way: OpConfirmCard. Styled by the ECONIC sidebar stylesheet (`.ea-op*`,
// apps/assistant/src/econic/sidebar/sidebar.css). WP-SIDEBAR.
import { useState, type ReactNode } from "react";
import { ArrowLeftRight, ArrowUpRight, Check, ChevronRight, Clock3, PencilLine, RotateCcw, ShieldCheck, Sparkles, Undo2, X } from "lucide-react";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { notifySaveFailed, notifySaved } from "@ncp/ui";
import { StagedLetters } from "./StagedLetters";
import { useUndoLayout } from "../hooks";
import type { ToolConfirmation, UiEvent } from "../types";
import { humanise } from "./labels";

const k = (key: string) => `assistant:econic.sidebar.op.${key}`;

/** The sentences of a change summary ("CPI outlook shown as bars; Trade moved to the top.") as separate lines. In Arabic text a
 *  transition reads the reading way ("خط ← أعمدة", APPLE-SPEC §10.2). */
export function summaryLines(text: string, ar = false): string[] {
  return text
    .split(/;\s+|؛\s*/)
    // The server writes widget names in Markdown emphasis (*CPI outlook*); a card shows plain words.
    .map((x) => x.replace(/\*\*([^*]+)\*\*|\*([^*]+)\*|_([^_]+)_/g, (_m, a, b, c) => a ?? b ?? c).trim().replace(/[.。]$/, ""))
    .map((x) => (ar ? x.replace(/\s*(?:→|->)\s*/g, " ← ") : x))
    .filter(Boolean)
    .slice(0, 6);
}

export interface OpCardProps {
  event: UiEvent;
  /** The latest change of this surface in the thread: only it offers Undo (Undo always undoes the last change). */
  latest: boolean;
  /** The screen's name for the "Where" line ("Today"), when the host knows it. */
  where?: string | null;
  /** Opens a route of the host (a department draft's preview, the screen that changed). */
  onNavigate?: (route: string) => void;
  /** Sends a follow-up message in the same thread ("Make the same change for my department", "Yes, send it for approval"). */
  onSend?: (text: string, options?: { scope?: "department" }) => void;
  /** Tells the host the screen changed again (the Undo result), so it refreshes and rings the widgets. */
  onChanged?: (event: UiEvent) => void;
}

export function OpCard({ event, latest, where, onNavigate, onSend, onChanged }: OpCardProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const undo = useUndoLayout();
  const [undone, setUndone] = useState(event.status === "undone");
  const lines = summaryLines(ar ? event.summaryAr || event.summary : event.summary || event.summaryAr, ar);
  const title = lines[0] ?? t(k("changed"), { defaultValue: "Screen changed" });
  const changes = lines.slice(1);
  const department = event.scope === "department";
  const status = undone ? "undone" : event.status;
  const route = previewOrRoute(event);

  const doUndo = () => {
    undo.mutate({ surface: event.surface, space: event.space ?? null }, {
      onSuccess: (r) => {
        setUndone(true);
        notifySaved(r, t(k("undoneToast"), { defaultValue: "Change undone" }));
        onChanged?.({ ...event, status: "undone" });
      },
      onError: (e) => notifySaveFailed(e, t(k("undoFailed"), { defaultValue: "The change could not be undone" })),
    });
  };

  let state: ReactNode;
  let actions: ReactNode = null;
  if (status === "applied") {
    state = <span className="ea-op-st applied"><Check aria-hidden />{t(k("appliedForYou"), { defaultValue: "Applied for you" })}</span>;
    actions = (
      <>
        {latest && !department ? <button type="button" className="ec-btn plain sm" disabled={undo.isPending} onClick={doUndo}>{t(k("undo"), { defaultValue: "Undo" })}</button> : null}
        {route && onNavigate ? (
          <button type="button" className="ea-icon" title={t(k("compare"), { defaultValue: "Show it on the screen" })} aria-label={t(k("compare"), { defaultValue: "Show it on the screen" })} onClick={() => onNavigate(route)}><ArrowLeftRight aria-hidden /></button>
        ) : null}
        {!department && onSend ? (
          <button type="button" className="ec-btn secondary sm" onClick={() => onSend(t(k("proposePrompt"), { defaultValue: "Make the same change for my department." }), { scope: "department" })}>
            {t(k("propose"), { defaultValue: "Propose to department" })}
          </button>
        ) : null}
      </>
    );
  } else if (status === "drafted") {
    state = <span className="ea-op-st draft"><Sparkles aria-hidden />{t(k("draftNotApplied"), { defaultValue: "Draft · not applied" })}</span>;
    actions = (
      <>
        {event.previewRoute && onNavigate ? <button type="button" className="ec-btn secondary sm" onClick={() => onNavigate(event.previewRoute!)}>{t(k("preview"), { defaultValue: "Preview" })}</button> : null}
        {latest && onSend ? (
          <button type="button" className="ec-btn primary sm" onClick={() => onSend(t(k("sendPrompt"), { defaultValue: "Yes, send it for approval." }), { scope: "department" })}>
            {t(k("sendForApproval"), { defaultValue: "Send for approval" })}
          </button>
        ) : null}
      </>
    );
  } else if (status === "proposed") {
    state = <span className="ea-op-st awaiting"><Clock3 aria-hidden />{t(k("awaiting"), { defaultValue: "Awaiting the Director" })}</span>;
    actions = onNavigate ? <button type="button" className="ec-btn secondary sm" onClick={() => onNavigate("/inbox")}>{t(k("openInbox"), { defaultValue: "Open the inbox" })}</button> : null;
  } else if (status === "published") {
    state = <span className="ea-op-st applied"><Check aria-hidden />{t(k("published"), { defaultValue: "Published for the department" })}</span>;
  } else if (status === "restored") {
    state = <span className="ea-op-st applied"><RotateCcw aria-hidden />{t(k("restored"), { defaultValue: "Earlier version restored" })}</span>;
  } else {
    state = <span className="ea-op-st muted"><Undo2 aria-hidden />{t(k("undone"), { defaultValue: "Undone" })}</span>;
  }

  return (
    <div className="ea-op" data-status={status} data-scope={event.scope}>
      <div className="ea-op-h">
        <PencilLine aria-hidden className="ic" />
        <span className="t" dir="auto">{title}</span>
        {event.version ? <span className="rev"><bdi dir="ltr">{t(k("rev"), { defaultValue: "rev {{n}}", n: event.version })}</bdi></span> : null}
      </div>
      <div className="ea-op-b">
        {changes.map((c, i) => (
          <div key={i} className="ea-op-line"><span className="k">{i === 0 ? t(k("also"), { defaultValue: "Also" }) : ""}</span><span className="v" dir="auto">{c}</span></div>
        ))}
        {where ? <div className="ea-op-line"><span className="k">{t(k("where"), { defaultValue: "Where" })}</span><span className="v">{where}</span></div> : null}
        <div className="ea-op-line">
          <span className="k">{t(k("scope"), { defaultValue: "Scope" })}</span>
          <span className="v">{department
            ? t(k("scopeDepartment"), { defaultValue: "Your department · a Director approves, never the requester" })
            : event.scope === "global"
              ? t(k("scopeGlobal"), { defaultValue: "Everyone · platform default" })
              : t(k("scopeMe"), { defaultValue: "Just you · runs with your access" })}</span>
        </div>
      </div>
      <details className="ea-details">
        <summary><ChevronRight aria-hidden className="chev" />{t(k("details"), { defaultValue: "Technical details" })}</summary>
        <div className="dbody" dir="ltr">
          <span><span className="dk">{t(k("surface"), { defaultValue: "Surface" })}</span> <code>{event.surface}</code></span>
          {event.version ? <span><span className="dk">{t(k("version"), { defaultValue: "Version" })}</span> <code>{event.version}</code></span> : null}
          {event.changed.length ? <span><span className="dk">{t(k("widgets"), { defaultValue: "Widgets" })}</span> <code>{event.changed.join(", ")}</code></span> : null}
          {event.draftId ? <span><span className="dk">{t(k("draft"), { defaultValue: "Draft" })}</span> <code>{event.draftId}</code></span> : null}
          {event.proposalId ? <span><span className="dk">{t(k("proposal"), { defaultValue: "Proposal" })}</span> <code>{event.proposalId}</code></span> : null}
        </div>
      </details>
      <OpFooter state={state} actions={actions} />
    </div>
  );
}

/** The op-card footer rule: status text at the start, one action group at the end (nowrap; it wraps as a unit), the default last. */
export function OpFooter({ state, actions }: { state: ReactNode; actions?: ReactNode }) {
  return (
    <div className="ea-op-f">
      {state}
      {actions ? <span className="ea-op-acts">{actions}</span> : null}
    </div>
  );
}

/** Visible card fields the user may reword before confirming; the server applies edits to these (never to the `_` targets). */
const EDITABLE_ARGS = new Set(["title", "summary", "note", "category"]);

/**
 * An action the agent staged, waiting for the person (the inspector's form of the NCP confirmation card): the action's title, what it
 * does as key/value rows, the tool and expiry under "Technical details", then "✦ Draft · not applied" with Decline · Edit · Confirm (the
 * default last). Once decided it keeps its title and says what happened (✓ Done · Open, Declined, Expired, Failed).
 */
export function OpConfirmCard({ confirmation: c, locale, onDecide, onOpenLink, debug = false }: {
  confirmation: ToolConfirmation;
  locale: string;
  onDecide?: (c: ToolConfirmation, accept: boolean, edits?: Record<string, unknown>) => Promise<void>;
  onOpenLink?: (link: string) => void;
  debug?: boolean;
}) {
  const { t } = useTranslation();
  const ar = locale === "ar";
  const [busy, setBusy] = useState<"yes" | "no" | null>(null);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const title = plain(ar && c.titleAr ? c.titleAr : c.title);
  const description = plain((ar && c.descriptionAr ? c.descriptionAr : c.description) ?? "");
  // What a person reads: no internal ids (`_x`, `…Id`), and of a bilingual pair (`summary` / `summaryAr`) the reader's language only.
  const fields = Object.entries(c.arguments).filter(([key, v]) => !key.startsWith("_") && !/(Id|Ids)$/.test(key) && v !== null && v !== undefined && v !== ""
    && !(ar ? !key.endsWith("Ar") && typeof c.arguments[`${key}Ar`] === "string" && c.arguments[`${key}Ar`] !== "" : key.endsWith("Ar") && key.length > 2 && c.arguments[key.slice(0, -2)] !== undefined));
  const editable = Object.entries(c.arguments).filter(([key, v]) => EDITABLE_ARGS.has(key) && (v === null || typeof v === "string"));
  const expired = c.status === "Pending" && new Date(c.expiresAt).getTime() <= Date.now();
  const pending = c.status === "Pending" && !expired;

  const run = async (accept: boolean) => {
    if (!onDecide) return;
    setBusy(accept ? "yes" : "no");
    const changed = Object.fromEntries(Object.entries(edits).filter(([key, v]) => v !== (c.arguments[key] ?? "")));
    try { await onDecide(c, accept, accept && Object.keys(changed).length > 0 ? changed : undefined); } finally { setBusy(null); }
  };

  const status = expired ? "Expired" : c.status;
  const word = t(`ai.chat.confirmStatus.${status}`, { defaultValue: status });
  const state = pending
    ? <span className="ea-op-st draft"><Sparkles aria-hidden />{t(k("draftNotApplied"), { defaultValue: "Draft · not applied" })}</span>
    : status === "Executed" ? <span className="ea-op-st applied"><Check aria-hidden />{word}</span>
    : status === "Failed" ? <span className="ea-op-st failed"><X aria-hidden />{c.error ? `${word} · ${c.error}` : word}</span>
    : status === "Confirmed" ? <span className="ea-op-st muted"><Clock3 aria-hidden />{word}</span>
    : <span className="ea-op-st muted">{word}</span>;
  const link = status === "Executed" ? c.result?.link : null;
  const actions = pending && onDecide ? (
    <>
      <button type="button" className="ec-btn plain sm" disabled={busy !== null} onClick={() => void run(false)}>{t("ai.chat.decline")}</button>
      {editable.length > 0 ? <button type="button" className="ec-btn plain sm" disabled={busy !== null} aria-pressed={editing} onClick={() => setEditing((e) => !e)}>{editing ? t("ai.chat.confirmDoneEditing") : t("ai.chat.confirmEdit")}</button> : null}
      <button type="button" className="ec-btn primary sm" disabled={busy !== null} aria-busy={busy === "yes" || undefined} onClick={() => void run(true)}>{t("ai.chat.confirm")}</button>
    </>
  ) : link && onOpenLink ? (
    <button type="button" className="ec-btn secondary sm" onClick={() => onOpenLink(link)}><ArrowUpRight aria-hidden />{t("ai.chat.openResult")}</button>
  ) : null;

  return (
    <div className="ea-op" data-status={pending ? "drafted" : status.toLowerCase()} data-confirm="">
      <div className="ea-op-h">
        <ShieldCheck aria-hidden className="ic" />
        <span className="t" dir="auto">{title}</span>
      </div>
      {pending ? (
        <div className="ea-op-b">
          {editing ? editable.map(([key, v]) => (
            <label key={key} className="ea-op-edit">
              <span className="k">{t(`ai.chat.confirmArg.${key}`, { defaultValue: humanise(key) })}</span>
              {key === "summary" || key === "note"
                ? <textarea dir="auto" rows={3} value={edits[key] ?? String(v ?? "")} onChange={(e) => setEdits((s) => ({ ...s, [key]: e.target.value }))} />
                : <input dir="auto" value={edits[key] ?? String(v ?? "")} onChange={(e) => setEdits((s) => ({ ...s, [key]: e.target.value }))} />}
            </label>
          )) : (
            <>
              {fields.map(([key, v]) => (
                <div key={key} className="ea-op-line">
                  <span className="k">{t(`ai.chat.confirmArg.${argLabel(key)}`, { defaultValue: humanise(argLabel(key)) })}</span>
                  <span className="v" dir="auto">{valueText(edits[key] ?? v)}</span>
                </div>
              ))}
              {description ? <div className="ea-op-line"><span className="k">{t(k("effect"), { defaultValue: "Effect" })}</span><span className="v" dir="auto">{description}</span></div> : null}
              <StagedLetters confirmation={c} className="mt-2 max-h-[50vh] overflow-y-auto" />
            </>
          )}
        </div>
      ) : null}
      {pending ? (
        <details className="ea-details">
          <summary><ChevronRight aria-hidden className="chev" />{t(k("details"), { defaultValue: "Technical details" })}</summary>
          <div className="dbody">
            <span><span className="dk">{t(k("action"), { defaultValue: "Action" })}</span> <code dir="ltr">{c.toolKey}</code></span>
            <span><span className="dk">{t(k("expires"), { defaultValue: "Expires" })}</span> {formatRelative(c.expiresAt, ar ? "ar" : "en")}</span>
            {debug ? <span><span className="dk">id</span> <code dir="ltr">{c.id}</code></span> : null}
          </div>
        </details>
      ) : null}
      <OpFooter state={state} actions={actions} />
    </div>
  );
}

/** A bilingual field (`summaryAr`) is labelled as its pair (`summary`): the reader sees one, in their language. */
const argLabel = (key: string) => (key.length > 2 && key.endsWith("Ar") ? key.slice(0, -2) : key);
/** Markdown emphasis the server puts around names (*CPI outlook*) reads as plain words on a card. */
const plain = (s: string) => s.replace(/\*\*([^*]+)\*\*|\*([^*]+)\*/g, (_m, a, b) => a ?? b);
function valueText(v: unknown): string {
  if (typeof v === "string") return plain(v);
  if (Array.isArray(v)) return v.map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x))).join(", ");
  if (v && typeof v === "object") return Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== null && x !== "").map(([key, x]) => `${humanise(key)}: ${typeof x === "object" ? JSON.stringify(x) : String(x)}`).join(" · ");
  return String(v);
}

/** Where a change can be seen: a draft's preview, else the screen of its surface. */
export function previewOrRoute(e: Pick<UiEvent, "surface" | "previewRoute">): string | null {
  if (e.previewRoute) return e.previewRoute;
  return routeOfSurface(e.surface);
}

/** The route of a surface key (builder §2.1): dashboard → `/`, `module:<m>:<tab>` → `/m/<m>[/<tab>]`, knowledge → `/knowledge`, … */
export function routeOfSurface(surface: string): string | null {
  if (surface.startsWith("dashboard:")) return "/";
  if (surface === "workspaces") return "/workspaces";
  if (surface === "tasks") return "/tasks";
  if (surface === "trust") return "/trust";
  const m = /^module:([^:]+):([^:]+)$/.exec(surface);
  if (m) {
    const [, mod, tab] = m;
    if (mod === "knowledge") return tab === "home" ? "/knowledge" : `/knowledge/${tab}`;
    if (mod === "intelligence") return "/intelligence";
    return tab === "overview" ? `/m/${mod}` : `/m/${mod}/${tab}`;
  }
  const app = /^app:[^:]+:(.+)$/.exec(surface);
  if (app) return `/p/${app[1]}`;
  return null;
}
